import * as React from 'react'
import { useEffect, useState } from 'react'

import type { NotesActivityLevel } from '@disco/api/server'

import DocumentHeadTags from 'components/document-head-tags'
import {
  Box,
  Checkbox,
  Flex,
  Heading,
  RadioGroup,
  Switch,
  Text,
} from 'components/ds'
import Button from 'components/ui/button'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

import { useNotesNotificationPreference } from './use-notes-notification-preference'

const ACTIVITY_OPTIONS: {
  value: NotesActivityLevel
  label: string
  description: string
}[] = [
  {
    value: 'all',
    label: 'All notes',
    description: "Notify me about all new notes on tracks I'm following",
  },
  {
    value: 'mentions_and_replies',
    label: 'Mentions and replies only',
    description:
      "Only notify me when I'm mentioned or someone replies to my note",
  },
  {
    value: 'off',
    label: 'Off',
    description: "Don't notify me about track note activity",
  },
]

const OptionText = ({
  label,
  description,
  muted,
}: {
  label: string
  description: string
  muted?: boolean
}): React.ReactElement => (
  <Box>
    <Text size="2" weight="medium" as="div" color={muted ? 'gray' : undefined}>
      {label}
    </Text>
    <Text size="1" color="gray" as="div">
      {description}
    </Text>
  </Box>
)

const NotesNotificationsSettings = (): React.ReactElement => {
  const { preference, loading, saving, save } = useNotesNotificationPreference()
  const [draftPreference, setDraftPreference] = useState(preference)

  // Seed the draft on first load only, never re-sync from a save result: an edit
  // made while a save is in flight must not be clobbered by the server response.
  useEffect(() => {
    setDraftPreference(current => current ?? preference)
  }, [preference])

  const channelsDisabled =
    !draftPreference || draftPreference.activity_level === 'off'
  const dirty =
    !!draftPreference &&
    !!preference &&
    (draftPreference.activity_level !== preference.activity_level ||
      draftPreference.channel_in_app !== preference.channel_in_app ||
      draftPreference.channel_email !== preference.channel_email ||
      draftPreference.auto_follow !== preference.auto_follow)

  return (
    <>
      <DocumentHeadTags title="Notifications | DISCO" />
      <PageHeader title="Notifications" />
      <PageContainer>
        {loading || !draftPreference ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : (
          <Flex direction="column" gap="6">
            <Box>
              <Heading size="3" mb="1">
                Track note activity
              </Heading>
              <Text size="2" color="gray" mb="3" as="div">
                Choose what activity you get notified about on tracks you&#39;re
                following.
              </Text>
              <RadioGroup.Root
                value={draftPreference.activity_level}
                onValueChange={(value: NotesActivityLevel) =>
                  setDraftPreference({
                    ...draftPreference,
                    activity_level: value,
                  })
                }
              >
                <Flex direction="column" gap="3">
                  {ACTIVITY_OPTIONS.map(option => (
                    <RadioGroup.Item key={option.value} value={option.value}>
                      <OptionText
                        label={option.label}
                        description={option.description}
                      />
                    </RadioGroup.Item>
                  ))}
                </Flex>
              </RadioGroup.Root>
            </Box>

            <Box>
              <Heading size="3" mb="1">
                Automatically follow tracks
              </Heading>
              <Text size="2" color="gray" mb="3" as="div">
                When enabled, you&#39;ll automatically receive notifications for
                any track you leave a note on.
              </Text>
              <Flex gap="3" align="start">
                <Switch
                  aria-label="Automatically follow all tracks after leaving a note"
                  checked={draftPreference.auto_follow}
                  onCheckedChange={checked =>
                    setDraftPreference({
                      ...draftPreference,
                      auto_follow: checked,
                    })
                  }
                />
                <OptionText
                  label="Enable"
                  description="Automatically follow all tracks after leaving a note"
                />
              </Flex>
            </Box>

            <Box>
              <Heading size="3" mb="1">
                How to notify me
              </Heading>
              <Text size="2" color="gray" mb="3" as="div">
                Choose where you receive notifications.
              </Text>
              <Flex direction="column" gap="3">
                <Text as="label" size="2">
                  <Flex gap="2" align="start">
                    <Checkbox
                      aria-label="In-app notifications"
                      checked={draftPreference.channel_in_app}
                      disabled={channelsDisabled}
                      onCheckedChange={checked =>
                        setDraftPreference({
                          ...draftPreference,
                          channel_in_app: checked === true,
                        })
                      }
                    />
                    <OptionText
                      label="In-app notifications"
                      description="See notifications inside of DISCO"
                      muted={channelsDisabled}
                    />
                  </Flex>
                </Text>
                <Text as="label" size="2">
                  <Flex gap="2" align="start">
                    <Checkbox
                      aria-label="Email"
                      checked={draftPreference.channel_email}
                      disabled={channelsDisabled}
                      onCheckedChange={checked =>
                        setDraftPreference({
                          ...draftPreference,
                          channel_email: checked === true,
                        })
                      }
                    />
                    <OptionText
                      label="Email"
                      description="Receive notifications to your email address"
                      muted={channelsDisabled}
                    />
                  </Flex>
                </Text>
              </Flex>
            </Box>

            <Box>
              <Button
                appearance="primary"
                caption="Save preferences"
                disabled={!dirty}
                progressIndicator={saving}
                onClick={() => save(draftPreference)}
              />
            </Box>
          </Flex>
        )}
      </PageContainer>
    </>
  )
}

export default NotesNotificationsSettings
